import React, { useEffect, useMemo, useState } from "react";
import api, { API, formatApiErrorDetail } from "@/lib/api";
import { useHasPerm } from "@/components/PermissionGate";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Tabs, TabsList, TabsTrigger, TabsContent,
} from "@/components/ui/tabs";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Plus, Trash2, ArrowUpCircle, ArrowLeftRight, CheckCircle2, FileText } from "lucide-react";
import { toast } from "sonner";

const EMPTY_LINE = { prodotto_id: "", quantita: "" };

// Helper: trigger a server PDF download for given movement IDs
async function downloadBulkPdf(ids) {
  if (!ids || !ids.length) return;
  try {
    const token = localStorage.getItem("ds_token");
    const res = await fetch(`${API}/movements/bulk-pdf`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify({ ids }),
    });
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const blob = await res.blob();
    const cd = res.headers.get("content-disposition") || "";
    const m = cd.match(/filename="?([^";]+)"?/);
    const a = document.createElement("a");
    const url = URL.createObjectURL(blob);
    a.href = url;
    a.download = m ? m[1] : `movimenti.pdf`;
    document.body.appendChild(a); a.click(); a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 1000);
  } catch (err) {
    toast.error(`Errore download PDF: ${err.message || err}`);
  }
}

function PreviewDialog({ open, onOpenChange, title, summaryRows, footerInfo, busy, onConfirm }) {
  return (
    <Dialog open={open} onOpenChange={(o) => !busy && onOpenChange(o)}>
      <DialogContent className="max-w-2xl" data-testid="mov-preview-dialog">
        <DialogHeader>
          <DialogTitle className="font-brand text-2xl font-light">{title}</DialogTitle>
          <DialogDescription>
            Verifica le quantità prima di confermare. Dopo la conferma verrà generato un PDF riassuntivo.
          </DialogDescription>
        </DialogHeader>

        <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--ds-border)" }}>
          <table className="w-full text-sm">
            <thead style={{ background: "var(--ds-bg)" }}>
              <tr>
                <th className="text-left py-2 px-3 label-tag">Prodotto</th>
                <th className="text-right py-2 px-3 label-tag">Disponibilità</th>
                <th className="text-right py-2 px-3 label-tag">Quantità</th>
              </tr>
            </thead>
            <tbody>
              {summaryRows.map((r, i) => (
                <tr key={`prev-${i}`} className="border-t" style={{ borderColor: "var(--ds-border)" }}>
                  <td className="py-2 px-3">{r.nome}</td>
                  <td className="py-2 px-3 text-right text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                    {r.stock != null ? r.stock : "—"}
                  </td>
                  <td className="py-2 px-3 text-right font-medium" style={{ color: "var(--ds-primary)" }}>
                    {r.quantita}
                  </td>
                </tr>
              ))}
              {summaryRows.length === 0 && (
                <tr><td colSpan={3} className="py-4 text-center text-sm" style={{ color: "var(--ds-text-secondary)" }}>
                  Nessuna riga
                </td></tr>
              )}
            </tbody>
            {summaryRows.length > 0 && (
              <tfoot style={{ background: "var(--ds-bg)" }}>
                <tr>
                  <td className="py-2 px-3 label-tag" colSpan={2}>Totale articoli</td>
                  <td className="py-2 px-3 text-right font-medium">
                    {summaryRows.reduce((s, r) => s + Number(r.quantita || 0), 0)}
                  </td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>

        {footerInfo && (
          <div className="text-xs mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2">
            {Object.entries(footerInfo).map(([k, v]) => v && (
              <div key={k}>
                <span className="label-tag">{k}:</span> <b>{v}</b>
              </div>
            ))}
          </div>
        )}

        <DialogFooter className="mt-4 gap-2">
          <Button type="button" variant="outline" disabled={busy} onClick={() => onOpenChange(false)} data-testid="mov-preview-cancel">
            Annulla
          </Button>
          <Button
            type="button"
            disabled={busy || summaryRows.length === 0}
            onClick={onConfirm}
            className="ds-btn-primary"
            style={{ background: "var(--ds-primary)" }}
            data-testid="mov-preview-confirm"
          >
            <FileText size={14} className="mr-1.5" />
            {busy ? "Operazione in corso…" : "Conferma e scarica PDF"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function LineEditor({ righe, setRighe, availableProducts, lockedHint, destProducts }) {
  const showDest = Array.isArray(destProducts);

  // Build a lookup by normalized name → quantity in destination warehouse
  const destStockByName = useMemo(() => {
    if (!showDest) return null;
    const map = new Map();
    (destProducts || []).forEach((p) => {
      const key = (p.nome_articolo || "").trim().toLowerCase();
      if (!key) return;
      map.set(key, (map.get(key) || 0) + (p.quantita || 0));
    });
    return map;
  }, [destProducts, showDest]);

  const destStockFor = (name) => {
    if (!destStockByName) return null;
    const key = (name || "").trim().toLowerCase();
    return destStockByName.has(key) ? destStockByName.get(key) : null;
  };
  // Auto-populate righe from availableProducts (sorted alphabetically) whenever deposit changes
  useEffect(() => {
    if (availableProducts.length === 0) {
      setRighe([{ ...EMPTY_LINE }]);
      return;
    }
    const sorted = [...availableProducts].sort((a, b) =>
      (a.nome_articolo || "").localeCompare(b.nome_articolo || "", "it")
    );
    setRighe(sorted.map((p) => ({ prodotto_id: p.id, quantita: "", _stock: p.quantita, _name: p.nome_articolo })));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [availableProducts.length, availableProducts.map((p) => p.id).join(",")]);

  const setLine = (idx, key, value) => {
    const next = [...righe];
    next[idx] = { ...next[idx], [key]: value };
    setRighe(next);
  };

  const [search, setSearch] = React.useState("");
  const filtered = useMemo(() => {
    if (!search.trim()) return righe;
    const q = search.toLowerCase();
    return righe.filter((r) => {
      const p = availableProducts.find((x) => x.id === r.prodotto_id);
      const name = r._name || p?.nome_articolo || "";
      return name.toLowerCase().includes(q);
    });
  }, [righe, search, availableProducts]);

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between gap-3">
        <div className="label-tag">Prodotti disponibili nel deposito ({availableProducts.length})</div>
        {availableProducts.length > 0 && (
          <Input
            placeholder="Cerca prodotto…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="rounded-lg max-w-xs"
            data-testid="mov-search"
          />
        )}
      </div>

      {!availableProducts.length && lockedHint && (
        <div className="text-sm py-3 px-4 rounded-lg border border-dashed" style={{ borderColor: "var(--ds-border)", color: "var(--ds-text-secondary)" }}>
          {lockedHint}
        </div>
      )}

      {availableProducts.length > 0 && (
        <div className="border rounded-lg overflow-hidden" style={{ borderColor: "var(--ds-border)" }}>
          <div className="grid grid-cols-12 gap-2 py-2 px-3 text-xs uppercase tracking-wider font-medium" style={{ background: "var(--ds-bg)", color: "var(--ds-text-secondary)" }}>
            <div className={showDest ? "col-span-5" : "col-span-7"}>Prodotto</div>
            <div className="col-span-2 text-right">{showDest ? "Disp. origine" : "Disponibile"}</div>
            {showDest && <div className="col-span-2 text-right">Disp. destinazione</div>}
            <div className="col-span-3">Da movimentare</div>
          </div>
          <div className="max-h-[420px] overflow-y-auto">
            {filtered.map((r) => {
              const origIdx = righe.findIndex((x) => x.prodotto_id === r.prodotto_id);
              const stock = r._stock != null ? r._stock : availableProducts.find((p) => p.id === r.prodotto_id)?.quantita || 0;
              const name = r._name || availableProducts.find((p) => p.id === r.prodotto_id)?.nome_articolo || "—";
              const q = parseFloat(r.quantita) || 0;
              const overflow = q > stock;
              const destQty = showDest ? destStockFor(name) : null;
              return (
                <div key={r.prodotto_id} className="grid grid-cols-12 gap-2 items-center py-2 px-3 border-t text-sm" style={{ borderColor: "var(--ds-border)" }} data-testid={`mov-row-${r.prodotto_id}`}>
                  <div className={showDest ? "col-span-5 font-medium" : "col-span-7 font-medium"}>{name}</div>
                  <div className="col-span-2 text-right" style={{ color: stock === 0 ? "var(--ds-text-secondary)" : "var(--ds-text-primary)" }}>{stock}</div>
                  {showDest && (
                    <div
                      className="col-span-2 text-right"
                      style={{ color: destQty === 0 ? "var(--ds-text-secondary)" : "var(--ds-text-primary)" }}
                      data-testid={`mov-dest-stock-${r.prodotto_id}`}
                      title={destQty === null ? "Prodotto assente nel magazzino di destinazione" : `Giacenza attuale nel magazzino di destinazione: ${destQty}`}
                    >
                      {destQty === null ? "—" : destQty}
                    </div>
                  )}
                  <div className="col-span-3">
                    <Input
                      type="number"
                      step="any"
                      min="0"
                      max={stock}
                      placeholder="0"
                      value={r.quantita === "" ? "" : r.quantita}
                      onChange={(e) => setLine(origIdx, "quantita", e.target.value === "" ? "" : parseFloat(e.target.value))}
                      className={`rounded-md h-8 ${overflow ? "border-red-500" : ""}`}
                      data-testid={`mov-qty-${r.prodotto_id}`}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

// ----------------- IN USCITA -----------------
function OutboundForm({ deposits, products, onDone }) {
  const [deposito, setDeposito] = useState("");
  const [note, setNote] = useState("");
  const [righe, setRighe] = useState([{ ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const available = useMemo(
    () => (deposito ? products.filter((p) => p.deposito_id === deposito) : []),
    [products, deposito]
  );

  const validRows = useMemo(
    () => righe.filter((r) => r.prodotto_id && parseFloat(r.quantita) > 0),
    [righe]
  );

  const summaryRows = useMemo(() => {
    return validRows.map((r) => {
      const p = products.find((x) => x.id === r.prodotto_id);
      return {
        nome: p?.nome_articolo || r._name || "—",
        stock: r._stock != null ? r._stock : p?.quantita ?? null,
        quantita: parseFloat(r.quantita) || 0,
      };
    });
  }, [validRows, products]);

  const depositoNome = deposits.find((d) => d.id === deposito)?.nome || "—";

  const onPreview = (e) => {
    e.preventDefault();
    if (!deposito) return toast.error("Seleziona un magazzino");
    if (validRows.length === 0) return toast.error("Inserisci la quantità per almeno un prodotto");
    setPreviewOpen(true);
  };

  const confirmAndSubmit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/movements/outbound", {
        deposito_id: deposito,
        note,
        righe: validRows.map((r) => ({ prodotto_id: r.prodotto_id, quantita: parseFloat(r.quantita) || 0 })),
      });
      toast.success(`${data.count} righe scaricate`);
      // Download PDF
      const ids = (data.movements || []).map((m) => m.id).filter(Boolean);
      if (ids.length) await downloadBulkPdf(ids);
      setRighe([{ ...EMPTY_LINE }]);
      setNote("");
      setPreviewOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <form onSubmit={onPreview} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label className="label-tag">Magazzino *</Label>
            <Select value={deposito} onValueChange={(v) => { setDeposito(v); setRighe([{ ...EMPTY_LINE }]); }}>
              <SelectTrigger className="mt-1.5 rounded-lg" data-testid="outbound-deposito">
                <SelectValue placeholder="Seleziona magazzino…" />
              </SelectTrigger>
              <SelectContent>
                {deposits.map((d) => (
                  <SelectItem key={d.id} value={d.id}>{d.nome} {d.preferito ? "★" : ""}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-tag">Note</Label>
            <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Es. Consumo banco, perdita…" className="mt-1.5 rounded-lg" />
          </div>
        </div>

        <LineEditor
          righe={righe}
          setRighe={setRighe}
          availableProducts={available}
          lockedHint={!deposito ? "Seleziona prima un magazzino per vedere i prodotti." : "Nessun prodotto in questo magazzino."}
        />

        <div className="flex justify-end">
          <Button type="submit" disabled={!deposito} className="ds-btn-primary" style={{ background: "var(--ds-primary)" }} data-testid="outbound-submit">
            <ArrowUpCircle size={16} className="mr-1.5" /> Anteprima scarico
          </Button>
        </div>
      </form>

      <PreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title="Anteprima scarico in uscita"
        summaryRows={summaryRows}
        footerInfo={{ Magazzino: depositoNome, Note: note }}
        busy={busy}
        onConfirm={confirmAndSubmit}
      />
    </>
  );
}

// ----------------- INTERNI -----------------
function TransferForm({ deposits, products, onDone }) {
  const [origine, setOrigine] = useState("");
  const [destinazione, setDestinazione] = useState("");
  const [note, setNote] = useState("");
  const [righe, setRighe] = useState([{ ...EMPTY_LINE }]);
  const [busy, setBusy] = useState(false);
  const [previewOpen, setPreviewOpen] = useState(false);

  const available = useMemo(
    () => (origine ? products.filter((p) => p.deposito_id === origine) : []),
    [products, origine]
  );

  const destAvailable = useMemo(
    () => (destinazione ? products.filter((p) => p.deposito_id === destinazione) : []),
    [products, destinazione]
  );

  const destOptions = deposits.filter((d) => d.id !== origine);

  const validRows = useMemo(
    () => righe.filter((r) => r.prodotto_id && parseFloat(r.quantita) > 0),
    [righe]
  );

  const summaryRows = useMemo(() => {
    return validRows.map((r) => {
      const p = products.find((x) => x.id === r.prodotto_id);
      return {
        nome: p?.nome_articolo || r._name || "—",
        stock: r._stock != null ? r._stock : p?.quantita ?? null,
        quantita: parseFloat(r.quantita) || 0,
      };
    });
  }, [validRows, products]);

  const origineNome = deposits.find((d) => d.id === origine)?.nome || "—";
  const destinazioneNome = deposits.find((d) => d.id === destinazione)?.nome || "—";

  const onPreview = (e) => {
    e.preventDefault();
    if (!origine) return toast.error("Seleziona il magazzino di origine");
    if (!destinazione) return toast.error("Seleziona il magazzino di destinazione");
    if (origine === destinazione) return toast.error("Origine e destinazione devono essere diverse");
    if (validRows.length === 0) return toast.error("Inserisci la quantità per almeno un prodotto");
    setPreviewOpen(true);
  };

  const confirmAndSubmit = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/movements/transfer", {
        deposito_origine_id: origine,
        deposito_destinazione_id: destinazione,
        note,
        righe: validRows.map((r) => ({ prodotto_id: r.prodotto_id, quantita: parseFloat(r.quantita) || 0 })),
      });
      toast.success(`${data.count / 2} righe trasferite`);
      const ids = (data.movements || []).map((m) => m.id).filter(Boolean);
      if (ids.length) await downloadBulkPdf(ids);
      setRighe([{ ...EMPTY_LINE }]);
      setNote("");
      setPreviewOpen(false);
      onDone();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <form onSubmit={onPreview} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div>
          <Label className="label-tag">Da magazzino *</Label>
          <Select value={origine} onValueChange={(v) => { setOrigine(v); setRighe([{ ...EMPTY_LINE }]); }}>
            <SelectTrigger className="mt-1.5 rounded-lg" data-testid="transfer-origine">
              <SelectValue placeholder="Origine…" />
            </SelectTrigger>
            <SelectContent>
              {deposits.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tag">A magazzino *</Label>
          <Select value={destinazione} onValueChange={setDestinazione}>
            <SelectTrigger className="mt-1.5 rounded-lg" data-testid="transfer-destinazione">
              <SelectValue placeholder="Destinazione…" />
            </SelectTrigger>
            <SelectContent>
              {destOptions.length === 0 && (
                <div className="px-3 py-2 text-xs" style={{ color: "var(--ds-text-secondary)" }}>
                  Seleziona prima un'origine
                </div>
              )}
              {destOptions.map((d) => (
                <SelectItem key={d.id} value={d.id}>{d.nome}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label className="label-tag">Note</Label>
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Motivo trasferimento…" className="mt-1.5 rounded-lg" />
        </div>
      </div>

      <LineEditor
        righe={righe}
        setRighe={setRighe}
        availableProducts={available}
        destProducts={destAvailable}
        lockedHint={!origine ? "Seleziona prima il magazzino di origine." : "Nessun prodotto in questo magazzino."}
      />

      <div className="flex justify-end">
        <Button
          type="submit"
          disabled={!origine || !destinazione}
          className="ds-btn-primary"
          style={{ background: "var(--ds-primary)" }}
          data-testid="transfer-submit"
        >
          <ArrowLeftRight size={16} className="mr-1.5" /> Anteprima trasferimento
        </Button>
      </div>
    </form>

      <PreviewDialog
        open={previewOpen}
        onOpenChange={setPreviewOpen}
        title="Anteprima trasferimento"
        summaryRows={summaryRows}
        footerInfo={{ "Da magazzino": origineNome, "A magazzino": destinazioneNome, Note: note }}
        busy={busy}
        onConfirm={confirmAndSubmit}
      />
    </>
  );
}

// ----------------- Page -----------------
export default function Movimentazioni() {
  const hasPerm = useHasPerm();
  const canCreate = hasPerm("movimentazioni:create");
  const [deposits, setDeposits] = useState([]);
  const [products, setProducts] = useState([]);

  const load = async () => {
    const [d, p] = await Promise.all([api.get("/deposits"), api.get("/products")]);
    setDeposits(d.data);
    setProducts(p.data);
  };
  useEffect(() => { load(); }, []);

  return (
    <div className="space-y-6" data-testid="movimentazioni-page">
      <div>
        <div className="label-tag mb-2">Operazioni</div>
        <h1 className="font-brand text-4xl sm:text-5xl font-light tracking-tight">Movimentazioni</h1>
        <p className="text-sm mt-2" style={{ color: "var(--ds-text-secondary)" }}>
          Registra scarichi in uscita e trasferimenti tra i tuoi magazzini.
        </p>
      </div>

      {!canCreate ? (
        <div className="ds-card p-8 text-center" data-testid="mov-read-only-banner">
          <h2 className="font-brand text-2xl font-light">Solo visualizzazione</h2>
          <p className="mt-3 text-sm" style={{ color: "var(--ds-text-secondary)" }}>
            Il tuo ruolo non ha il permesso di creare movimentazioni. Consulta lo storico movimenti dal menu laterale.
          </p>
        </div>
      ) : (
        <Tabs defaultValue="uscita">
        <TabsList className="bg-white border" style={{ borderColor: "var(--ds-border)" }}>
          <TabsTrigger value="uscita" data-testid="mov-tab-uscita">
            <ArrowUpCircle size={14} className="mr-1.5" /> In uscita
          </TabsTrigger>
          <TabsTrigger value="interni" data-testid="mov-tab-interni">
            <ArrowLeftRight size={14} className="mr-1.5" /> Interni
          </TabsTrigger>
        </TabsList>

        <TabsContent value="uscita" className="ds-card p-6 mt-4">
          <OutboundForm deposits={deposits} products={products} onDone={load} />
        </TabsContent>

        <TabsContent value="interni" className="ds-card p-6 mt-4">
          <TransferForm deposits={deposits} products={products} onDone={load} />
        </TabsContent>
      </Tabs>
      )}
    </div>
  );
}
